import React, { useState, useEffect, useCallback } from 'react';
import { Users, Search, Plus, X, Phone, Mail, MapPin, Loader2, Pencil, Clock, CheckCircle, Truck, UserCheck, Zap, Trash2, Download, Upload } from 'lucide-react';
import { customerAPI, approvalAPI, salespersonAPI, gstinAPI, fetchPaged, masterChangeAPI } from '../../services/api';
import Pagination from '../ui/Pagination';
import ContactImportWizard from '../ui/ContactImportWizard';
import { exportToCSV } from '../../utils/exportCSV';
import { toast } from 'sonner';

const emptyCustomer = { name: '', contact: '', phone: '', email: '', gstin: '', pan: '', state: '', city: '', pincode: '', address: '', type: 'Corporate', credit_days: 30, credit_limit: 0, payment_terms: '', salesperson_id: '', salesperson_name: '' };

const CustomerManager = () => {
  const [custs, setCusts] = useState([]);
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [formData, setFormData] = useState({ ...emptyCustomer });
  const [remarks, setRemarks] = useState('');
  const [pendingEdits, setPendingEdits] = useState({});
  const [salespeople, setSalespeople] = useState([]);
  const [showShipAddr, setShowShipAddr] = useState(null); // customer ID
  const [shipAddrForm, setShipAddrForm] = useState({ label: '', name: '', address: '', city: '', state: '', pin: '', contact_person: '', contact_phone: '' });
  const [shipAddresses, setShipAddresses] = useState([]);
  const [gstinFetching, setGstinFetching] = useState(false);
  const [gstinResult, setGstinResult] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleteReason, setDeleteReason] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const handlePageSizeChange = (v) => { setPageSize(v); setPage(1); };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [result, approvals, salespeopleData] = await Promise.all([
        fetchPaged('/sales/customers', { page, page_size: pageSize, search: search || undefined }),
        approvalAPI.getAll('Pending').catch(() => []),
        salespersonAPI.getAll().catch(() => []),
      ]);
      setCusts(result.items);
      setTotal(result.total);
      setTotalPages(result.total_pages);
      setSalespeople(salespeopleData);
      const editsMap = {};
      approvals.forEach(a => {
        if (a.request_type === 'Edit' && a.original_customer_id) {
          editsMap[a.original_customer_id] = a;
        }
      });
      setPendingEdits(editsMap);
    } catch (err) {
      console.error('Error fetching customers:', err);
      setError('Failed to load customers');
    } finally {
      setLoading(false);
    }
  }, [page, search, pageSize]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Search is now server-side via fetchPaged params
  // Reset to page 1 when search changes
  const handleSearchChange = (val) => { setSearch(val); setPage(1); };

  const submitDeleteRequest = async () => {
    if (!deleteConfirm) return;
    try {
      setDeleting(true);
      await masterChangeAPI.create({ entity_type: 'customer', entity_id: deleteConfirm.id, entity_name: deleteConfirm.name, action: 'delete', reason: deleteReason });
      toast.success('Delete request submitted for Admin approval');
      setDeleteConfirm(null); setDeleteReason('');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to submit'); }
    finally { setDeleting(false); }
  };


  const openAdd = () => {
    setEditingCustomer(null);
    setFormData({ ...emptyCustomer });
    setRemarks('');
    setError('');
    setGstinResult(null);
    setShowForm(true);
  };

  const openEdit = (customer) => {
    setEditingCustomer(customer);
    setFormData({
      name: customer.name || '', contact: customer.contact || '', phone: customer.phone || '',
      email: customer.email || '', gstin: customer.gstin || '', pan: customer.pan || '',
      state: customer.state || '', city: customer.city || '', pincode: customer.pincode || '',
      address: customer.address || '', type: customer.type || 'Corporate', credit_days: customer.credit_days || 30,
      credit_limit: customer.credit_limit || 0,
      payment_terms: customer.payment_terms || '', salesperson_id: customer.salesperson_id || '', salesperson_name: customer.salesperson_name || '',
    });
    setRemarks('');
    setError('');
    setGstinResult(null);
    setShowForm(true);
  };

  const handleGstinFetch = async () => {
    const gstin = (formData.gstin || '').trim().toUpperCase();
    if (!gstin || gstin.length !== 15) {
      setGstinResult({ error: 'Enter a valid 15-digit GSTIN first' });
      return;
    }
    setGstinFetching(true);
    setGstinResult(null);
    try {
      const data = await gstinAPI.lookup(gstin);
      setGstinResult(data);
      if (data.found) {
        setFormData(prev => ({
          ...prev,
          gstin: data.gstin || prev.gstin,
          name: data.name || data.legal_name || prev.name,
          pan: data.pan || prev.pan,
          state: data.state || prev.state,
          city: data.city || prev.city,
          pincode: data.pincode || prev.pincode,
          address: data.address || prev.address,
        }));
      } else {
        // Even offline, we get state + PAN
        if (data.state) setFormData(prev => ({ ...prev, state: data.state || prev.state, pan: data.pan || prev.pan }));
      }
    } catch (err) {
      setGstinResult({ error: err.response?.data?.detail || 'Lookup failed' });
    } finally {
      setGstinFetching(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) return setError('Company name is required');
    if (!formData.name.trim()) return setError('Name is required');
    try {
      setSubmitting(true);
      setError('');
      // Resolve salesperson name
      const spData = { ...formData, credit_days: Number(formData.credit_days), credit_limit: Number(formData.credit_limit) || 0 };
      if (spData.salesperson_id) {
        const sp = salespeople.find(s => s.id === spData.salesperson_id);
        if (sp) spData.salesperson_name = sp.name;
      }
      const payload = {
        request_type: editingCustomer ? 'Edit' : 'Add',
        customer_data: spData,
        original_customer_id: editingCustomer?.id || null,
        remarks,
      };
      await approvalAPI.create(payload);
      setShowForm(false);
      setSuccessMsg(editingCustomer ? 'Edit request submitted for approval' : 'Add request submitted for approval');
      setTimeout(() => setSuccessMsg(''), 4000);
      fetchData();
    } catch (err) {
      console.error('Error submitting request:', err);
      setError(err.response?.data?.detail || 'Failed to submit request');
    } finally {
      setSubmitting(false);
    }
  };

  const typeColors = { Government: 'bg-blue-50 text-blue-700', Corporate: 'bg-emerald-50 text-emerald-700', Retail: 'bg-purple-50 text-purple-700' };

  const openShipAddr = async (customerId) => {
    try {
      const addrs = await customerAPI.getShippingAddresses(customerId);
      setShipAddresses(addrs);
      setShowShipAddr(customerId);
      setShipAddrForm({ label: '', name: '', address: '', city: '', state: '', pin: '', contact_person: '', contact_phone: '' });
    } catch (err) { console.error(err); }
  };

  const addShipAddr = async () => {
    if (!shipAddrForm.name) return;
    try {
      await customerAPI.addShippingAddress(showShipAddr, shipAddrForm);
      const addrs = await customerAPI.getShippingAddresses(showShipAddr);
      setShipAddresses(addrs);
      setShipAddrForm({ label: '', name: '', address: '', city: '', state: '', pin: '', contact_person: '', contact_phone: '' });
    } catch (err) { alert('Failed to add address'); }
  };

  const removeShipAddr = async (addrId) => {
    if (!window.confirm('Remove this shipping address?')) return;
    try {
      await customerAPI.removeShippingAddress(showShipAddr, addrId);
      const addrs = await customerAPI.getShippingAddresses(showShipAddr);
      setShipAddresses(addrs);
    } catch (err) { alert('Failed to remove address'); }
  };

  const handleExportCustomers = async () => {
    try {
      setExporting(true);
      let allCustomers = [];
      let pg = 1;
      while (true) {
        const result = await fetchPaged('/sales/customers', { page: pg, page_size: 200 });
        allCustomers = allCustomers.concat(result.items);
        if (!result.has_next) break;
        pg++;
      }
      exportToCSV(allCustomers, [
        { label: 'Name', accessor: 'name' },
        { label: 'Contact', accessor: 'contact' },
        { label: 'Phone', accessor: 'phone' },
        { label: 'Email', accessor: 'email' },
        { label: 'GSTIN', accessor: 'gstin' },
        { label: 'PAN', accessor: 'pan' },
        { label: 'Address', accessor: 'address' },
        { label: 'City', accessor: 'city' },
        { label: 'State', accessor: 'state' },
        { label: 'Pincode', accessor: 'pincode' },
        { label: 'Type', accessor: 'type' },
        { label: 'Credit Days', accessor: 'credit_days' },
        { label: 'Credit Limit', accessor: 'credit_limit' },
        { label: 'Salesperson', accessor: 'salesperson_name' },
      ], 'buyers_master');
      toast.success(`Exported ${allCustomers.length} buyers`);
    } catch (err) { toast.error('Export failed'); }
    finally { setExporting(false); }
  };

  if (showImport) {
    return (
      <ContactImportWizard
        importType="Buyer"
        onBack={() => setShowImport(false)}
        onComplete={() => { setShowImport(false); fetchData(); }}
      />
    );
  }

  return (
    <>
      {successMsg && (
        <div className="mb-4 p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-xs text-emerald-700 flex items-center gap-2" data-testid="success-msg">
          <Clock className="w-3.5 h-3.5" /> {successMsg}
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-3 mb-5">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
          <input value={search} onChange={e => handleSearchChange(e.target.value)} placeholder="Search customers..."
            data-testid="customer-search"
            className="w-full pl-9 pr-4 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200" />
        </div>
        <div className="flex gap-2">
          <button data-testid="export-buyers-btn" onClick={handleExportCustomers} disabled={exporting}
            className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-gray-700 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50">
            {exporting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />} Export
          </button>
          <button data-testid="import-buyers-btn" onClick={() => setShowImport(true)}
            className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-gray-700 border border-gray-200 rounded-lg hover:bg-gray-50">
            <Upload className="w-3.5 h-3.5" /> Import
          </button>
          <button onClick={openAdd} data-testid="add-customer-btn"
            className="flex items-center gap-2 px-4 py-2 text-xs font-semibold text-white rounded-lg hover:shadow-lg hover:shadow-blue-200"
            style={{ backgroundColor: '#24549c' }}>
            <Plus className="w-3.5 h-3.5" /> Add Customer
          </button>
        </div>
      </div>

      {error && !showForm && <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-xs text-red-600">{error}</div>}

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
          <span className="ml-2 text-sm text-gray-500">Loading customers...</span>
        </div>
      ) : custs.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl border border-gray-100">
          <Users className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-sm text-gray-500">No customers found</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {custs.map(c => (
            <div key={c.id} className="bg-white rounded-xl border border-gray-100 p-4 hover:shadow-md transition-all relative" data-testid={`customer-card-${c.id}`}>
              {pendingEdits[c.id] && (
                <div className="absolute top-2 right-2 flex items-center gap-1 px-2 py-0.5 bg-amber-50 border border-amber-200 rounded-full">
                  <Clock className="w-2.5 h-2.5 text-amber-600" />
                  <span className="text-[9px] font-semibold text-amber-700">Edit Pending</span>
                </div>
              )}
              <div className="flex items-center justify-between mb-2">
                <span className={`inline-flex px-2 py-0.5 text-[10px] font-semibold rounded-full ${typeColors[c.type] || 'bg-gray-100 text-gray-600'}`}>{c.type}</span>
                <div className="flex items-center gap-1">
                  <button onClick={() => openEdit(c)} data-testid={`edit-customer-btn-${c.id}`}
                    className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors" title="Edit customer">
                    <Pencil className="w-3 h-3 text-gray-400 hover:text-gray-700" />
                  </button>
                  <button onClick={() => { setDeleteConfirm(c); setDeleteReason(''); }} data-testid={`delete-customer-btn-${c.id}`}
                    className="p-1.5 rounded-lg hover:bg-red-50 transition-colors" title="Request deletion">
                    <Trash2 className="w-3 h-3 text-gray-400 hover:text-red-600" />
                  </button>
                </div>
              </div>
              <h3 className="text-sm font-bold text-gray-900 mb-1" style={{ fontFamily: 'DM Sans' }}>{c.name}</h3>
              <p className="text-[11px] text-gray-500 mb-3 flex items-center gap-1"><Users className="w-3 h-3" />{c.contact}</p>
              <div className="space-y-1.5 text-[11px] text-gray-500">
                {c.phone && <p className="flex items-center gap-1.5"><Phone className="w-3 h-3" style={{ color: '#24549c' }} />{c.phone}</p>}
                {c.email && <p className="flex items-center gap-1.5"><Mail className="w-3 h-3" style={{ color: '#24549c' }} />{c.email}</p>}
                {c.address && <p className="flex items-start gap-1.5"><MapPin className="w-3 h-3 mt-0.5" style={{ color: '#24549c' }} />{c.address}</p>}
              </div>
              <div className="mt-3 pt-3 border-t border-gray-50 flex justify-between text-[10px]">
                <span className="text-gray-400">GSTIN: <span className="font-mono">{c.gstin || '-'}</span></span>
                <span className="text-gray-400">Credit: {c.credit_days}d {c.credit_limit > 0 ? `| Limit: INR ${Number(c.credit_limit).toLocaleString()}` : '| No limit'}</span>
              </div>
              {(c.salesperson_name || c.payment_terms) && (
                <div className="mt-1.5 flex flex-wrap gap-2 text-[10px]">
                  {c.salesperson_name && <span className="flex items-center gap-1 text-blue-600"><UserCheck className="w-2.5 h-2.5" />{c.salesperson_name}</span>}
                  {c.payment_terms && <span className="text-gray-400">| {c.payment_terms}</span>}
                </div>
              )}
              <div className="mt-2">
                <button
                  data-testid={`ship-addr-btn-${c.id}`}
                  onClick={() => openShipAddr(c.id)}
                  className="flex items-center gap-1 text-[10px] font-medium px-2 py-1 rounded-md hover:bg-blue-50 transition-colors"
                  style={{ color: '#24549c' }}
                >
                  <Truck className="w-3 h-3" /> Shipping Addresses ({(c.shipping_addresses || []).length})
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-gray-100 sticky top-0 bg-white rounded-t-2xl z-10">
              <div>
                <h2 className="text-base font-bold" style={{ fontFamily: 'DM Sans' }}>
                  {editingCustomer ? 'Edit Customer' : 'New Customer'}
                </h2>
                <p className="text-[11px] text-amber-600 mt-0.5 flex items-center gap-1">
                  <Clock className="w-3 h-3" /> Requires Manager/Admin approval
                </p>
              </div>
              <button onClick={() => setShowForm(false)} className="p-1 rounded-lg hover:bg-gray-100"><X className="w-4 h-4 text-gray-400" /></button>
            </div>
            <div className="p-5 space-y-3">
              {error && <div className="p-2.5 bg-red-50 border border-red-200 rounded-lg text-xs text-red-600">{error}</div>}
              <div><label className="block text-[11px] font-medium text-gray-700 mb-1">Company Name *</label><input value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} data-testid="cust-form-name" className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="block text-[11px] font-medium text-gray-700 mb-1">Contact Person</label><input value={formData.contact} onChange={e => setFormData({ ...formData, contact: e.target.value })} data-testid="cust-form-contact" className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200" /></div>
                <div><label className="block text-[11px] font-medium text-gray-700 mb-1">Phone</label><input value={formData.phone} onChange={e => setFormData({ ...formData, phone: e.target.value })} data-testid="cust-form-phone" className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="block text-[11px] font-medium text-gray-700 mb-1">Email</label><input value={formData.email} onChange={e => setFormData({ ...formData, email: e.target.value })} data-testid="cust-form-email" className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200" /></div>
                <div>
                  <label className="block text-[11px] font-medium text-gray-700 mb-1">GSTIN</label>
                  <div className="flex gap-1.5">
                    <input value={formData.gstin} onChange={e => { setFormData({ ...formData, gstin: e.target.value.toUpperCase() }); setGstinResult(null); }}
                      data-testid="cust-form-gstin" placeholder="e.g. 20AABCP1234A1ZV" maxLength={15}
                      className="flex-1 px-3 py-2 text-xs font-mono border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200 uppercase tracking-wider" />
                    <button type="button" onClick={handleGstinFetch} disabled={gstinFetching || !formData.gstin}
                      data-testid="gstin-fetch-btn"
                      className="flex items-center gap-1 px-3 py-2 text-[10px] font-bold text-white rounded-lg transition-all hover:opacity-90 disabled:opacity-40 whitespace-nowrap"
                      style={{ backgroundColor: '#24549c' }}>
                      {gstinFetching ? <Loader2 className="w-3 h-3 animate-spin" /> : <Zap className="w-3 h-3" />}
                      Fetch
                    </button>
                  </div>
                  {gstinResult && (
                    <div className={`mt-1.5 px-2.5 py-1.5 rounded-lg text-[10px] ${
                      gstinResult.error ? 'bg-red-50 text-red-700' :
                      gstinResult.found ? 'bg-green-50 text-green-700' :
                      'bg-amber-50 text-amber-700'
                    }`} data-testid="gstin-result">
                      {gstinResult.error ? (
                        <span>{gstinResult.error}</span>
                      ) : gstinResult.found ? (
                        <span><CheckCircle className="w-3 h-3 inline mr-1" /><strong>{gstinResult.legal_name}</strong> {gstinResult.trade_name && gstinResult.trade_name !== gstinResult.legal_name ? `(${gstinResult.trade_name})` : ''} — {gstinResult.status} | {gstinResult.state}</span>
                      ) : (
                        <span>{gstinResult.message || 'GSTIN not found in database'} {gstinResult.state && `| State: ${gstinResult.state}`}</span>
                      )}
                    </div>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="block text-[11px] font-medium text-gray-700 mb-1">PAN</label><input value={formData.pan} onChange={e => setFormData({ ...formData, pan: e.target.value })} placeholder="AABCP1234A" data-testid="cust-form-pan" className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200" /></div>
                <div><label className="block text-[11px] font-medium text-gray-700 mb-1">State</label><input value={formData.state} onChange={e => setFormData({ ...formData, state: e.target.value })} data-testid="cust-form-state" className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200" /></div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div><label className="block text-[11px] font-medium text-gray-700 mb-1">City</label><input value={formData.city} onChange={e => setFormData({ ...formData, city: e.target.value })} data-testid="cust-form-city" className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200" /></div>
                <div><label className="block text-[11px] font-medium text-gray-700 mb-1">Pincode</label><input value={formData.pincode} onChange={e => setFormData({ ...formData, pincode: e.target.value })} data-testid="cust-form-pincode" className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200" /></div>
                <div><label className="block text-[11px] font-medium text-gray-700 mb-1">Type</label><select value={formData.type} onChange={e => setFormData({ ...formData, type: e.target.value })} data-testid="cust-form-type" className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200"><option>Corporate</option><option>Government</option><option>Retail</option></select></div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div><label className="block text-[11px] font-medium text-gray-700 mb-1">Credit Days</label><input type="number" value={formData.credit_days} onChange={e => setFormData({ ...formData, credit_days: e.target.value })} data-testid="cust-form-credit" className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200" /></div>
                <div><label className="block text-[11px] font-medium text-gray-700 mb-1">Credit Limit (INR)</label><input type="number" value={formData.credit_limit} onChange={e => setFormData({ ...formData, credit_limit: e.target.value })} placeholder="0 = no limit" data-testid="cust-form-credit-limit" className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200" /></div>
                <div><label className="block text-[11px] font-medium text-gray-700 mb-1">Payment Terms</label>
                  <select value={formData.payment_terms} onChange={e => setFormData({ ...formData, payment_terms: e.target.value })} data-testid="cust-form-payment-terms" className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200">
                    <option value="">Select</option>
                    <option value="Advance">Advance</option>
                    <option value="COD">COD (Cash on Delivery)</option>
                    <option value="30 days credit">30 Days Credit</option>
                    <option value="45 days credit">45 Days Credit</option>
                    <option value="60 days credit">60 Days Credit</option>
                    <option value="90 days credit">90 Days Credit</option>
                    <option value="LC">Letter of Credit</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-[11px] font-medium text-gray-700 mb-1">Assigned Salesperson</label>
                <select value={formData.salesperson_id} onChange={e => setFormData({ ...formData, salesperson_id: e.target.value })} data-testid="cust-form-salesperson" className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200">
                  <option value="">No salesperson assigned</option>
                  {salespeople.map(sp => <option key={sp.id} value={sp.id}>{sp.name}{sp.phone ? ` (${sp.phone})` : sp.email ? ` (${sp.email})` : ''}</option>)}
                </select>
              </div>
              <div><label className="block text-[11px] font-medium text-gray-700 mb-1">Address</label><textarea value={formData.address} onChange={e => setFormData({ ...formData, address: e.target.value })} rows={2} data-testid="cust-form-address" className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200" /></div>
              <div><label className="block text-[11px] font-medium text-gray-700 mb-1">Remarks (optional)</label><input value={remarks} onChange={e => setRemarks(e.target.value)} placeholder="Why are you adding/editing this customer?" data-testid="cust-form-remarks" className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200" /></div>
            </div>
            <div className="flex justify-end gap-2 p-5 border-t border-gray-100">
              <button onClick={() => setShowForm(false)} disabled={submitting} className="px-4 py-2 text-xs font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-50">Cancel</button>
              <button onClick={handleSubmit} disabled={submitting} data-testid="cust-form-submit"
                className="px-4 py-2 text-xs font-semibold text-white rounded-lg hover:shadow-lg hover:shadow-blue-200 disabled:opacity-50 flex items-center gap-2" style={{ backgroundColor: '#24549c' }}>
                {submitting ? <><Loader2 className="w-3 h-3 animate-spin" /> Submitting...</> : <><Clock className="w-3 h-3" /> Submit for Approval</>}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Shipping Addresses Modal */}
      {showShipAddr && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[85vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-gray-100 sticky top-0 bg-white rounded-t-2xl z-10">
              <div>
                <h2 className="text-base font-bold" style={{ fontFamily: 'DM Sans' }}>Shipping Addresses</h2>
                <p className="text-[10px] text-gray-500 mt-0.5">{custs.find(c => c.id === showShipAddr)?.name}</p>
              </div>
              <button onClick={() => setShowShipAddr(null)} className="p-1 rounded-lg hover:bg-gray-100"><X className="w-4 h-4 text-gray-400" /></button>
            </div>
            <div className="p-5 space-y-4">
              {/* Existing addresses */}
              {shipAddresses.length > 0 ? (
                <div className="space-y-2">
                  {shipAddresses.map(addr => (
                    <div key={addr.id} className="flex items-start justify-between p-3 bg-gray-50 rounded-lg border border-gray-100">
                      <div className="text-xs">
                        <p className="font-semibold text-gray-900">{addr.label || addr.name}</p>
                        <p className="text-gray-500">{addr.address}, {addr.city}, {addr.state} {addr.pin}</p>
                        {addr.contact_person && <p className="text-gray-400 mt-0.5">{addr.contact_person} {addr.contact_phone && `- ${addr.contact_phone}`}</p>}
                      </div>
                      <button onClick={() => removeShipAddr(addr.id)} className="p-1 text-red-400 hover:text-red-600 hover:bg-red-50 rounded"><X className="w-3 h-3" /></button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-gray-400 text-center py-3">No shipping addresses added yet</p>
              )}

              {/* Add new address */}
              <div className="border-t border-gray-100 pt-3">
                <p className="text-[11px] font-semibold text-gray-700 mb-2">Add New Address</p>
                <div className="space-y-2">
                  <div className="grid grid-cols-2 gap-2">
                    <input data-testid="ship-addr-label" value={shipAddrForm.label} onChange={e => setShipAddrForm({ ...shipAddrForm, label: e.target.value })} placeholder="Label (e.g., Head Office)" className="px-2 py-1.5 text-xs border border-gray-200 rounded-lg" />
                    <input data-testid="ship-addr-name" value={shipAddrForm.name} onChange={e => setShipAddrForm({ ...shipAddrForm, name: e.target.value })} placeholder="Receiver Name *" className="px-2 py-1.5 text-xs border border-gray-200 rounded-lg" />
                  </div>
                  <input value={shipAddrForm.address} onChange={e => setShipAddrForm({ ...shipAddrForm, address: e.target.value })} placeholder="Street Address" className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded-lg" />
                  <div className="grid grid-cols-3 gap-2">
                    <input value={shipAddrForm.city} onChange={e => setShipAddrForm({ ...shipAddrForm, city: e.target.value })} placeholder="City" className="px-2 py-1.5 text-xs border border-gray-200 rounded-lg" />
                    <input value={shipAddrForm.state} onChange={e => setShipAddrForm({ ...shipAddrForm, state: e.target.value })} placeholder="State" className="px-2 py-1.5 text-xs border border-gray-200 rounded-lg" />
                    <input value={shipAddrForm.pin} onChange={e => setShipAddrForm({ ...shipAddrForm, pin: e.target.value })} placeholder="PIN" className="px-2 py-1.5 text-xs border border-gray-200 rounded-lg" />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <input value={shipAddrForm.contact_person} onChange={e => setShipAddrForm({ ...shipAddrForm, contact_person: e.target.value })} placeholder="Contact Person" className="px-2 py-1.5 text-xs border border-gray-200 rounded-lg" />
                    <input value={shipAddrForm.contact_phone} onChange={e => setShipAddrForm({ ...shipAddrForm, contact_phone: e.target.value })} placeholder="Phone" className="px-2 py-1.5 text-xs border border-gray-200 rounded-lg" />
                  </div>
                  <button data-testid="add-ship-addr-btn" onClick={addShipAddr} className="flex items-center gap-1 px-3 py-1.5 text-xs font-semibold text-white rounded-lg" style={{ backgroundColor: '#24549c' }}>
                    <Plus className="w-3 h-3" /> Add Address
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {custs.length > 0 && <Pagination page={page} totalPages={totalPages} total={total} pageSize={pageSize} onPageChange={setPage} onPageSizeChange={handlePageSizeChange} />}

      {/* Delete Confirmation */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50" onClick={() => setDeleteConfirm(null)}>
          <div className="bg-white rounded-xl shadow-xl w-96 p-5" onClick={e => e.stopPropagation()}>
            <div className="flex items-center gap-2 mb-3">
              <div className="p-2 bg-red-100 rounded-full"><Trash2 className="w-4 h-4 text-red-600" /></div>
              <div><h3 className="text-sm font-bold text-gray-900">Delete Customer</h3><p className="text-[10px] text-gray-500">Requires Admin approval</p></div>
            </div>
            <p className="text-xs text-gray-700 mb-3">Request deletion of <strong>{deleteConfirm.name}</strong>? The record will be deactivated upon approval.</p>
            <textarea value={deleteReason} onChange={e => setDeleteReason(e.target.value)} rows={2} placeholder="Reason for deletion..." data-testid="delete-customer-reason"
              className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-red-200" />
            <div className="flex justify-end gap-2 mt-3">
              <button onClick={() => setDeleteConfirm(null)} className="px-3 py-1.5 text-[11px] font-semibold text-gray-600 bg-gray-100 rounded-lg">Cancel</button>
              <button onClick={submitDeleteRequest} disabled={deleting} data-testid="confirm-delete-customer"
                className="px-3 py-1.5 text-[11px] font-semibold text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50">
                {deleting ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Submit Delete Request'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CustomerManager;
